<!--
  YesNoField — the segmented choice control for the whole field kit.

  Defaults to the paper's Y/N column (SqlBool 1/0, null = not answered), but
  takes an `options` list so the same control serves:
    - chute test `accepted`, calibration stability Y/N        (SqlBool)
    - ITP item status  done / N-A / failed                    (ItpStatus)
    - end-of-pour item Yes / No / N-A                         (EndOfPourStatus)

  One tap sets a value; tapping the selected option again clears it back to
  unanswered, so a mis-tap with a glove costs one tap, not a dialog. There are
  no modal confirmations anywhere in this control.
-->
<script lang="ts" generics="T extends string | number">
  import type { SqlBool } from '../types';

  /**
   * Not exported: Svelte's instance script cannot export types in runes mode.
   * Callers pass plain object literals; structural typing does the rest.
   */
  interface SegmentOption<V> {
    value: V;
    label: string;
    /** Colour when selected. */
    tone?: 'good' | 'bad' | 'warn' | 'neutral';
  }

  interface Props {
    label: string;
    value: T | null;
    options?: SegmentOption<T>[];
    /** Tapping the selected option clears it back to null (unanswered). */
    clearable?: boolean;
    disabled?: boolean;
    hideLabel?: boolean;
    compact?: boolean;
    /** Stretch the buttons to fill the row (default) or hug their text. */
    fill?: boolean;
    onValue?: (v: T | null) => void;
  }

  const YES_NO = [
    { value: 1 as SqlBool, label: 'Yes', tone: 'good' },
    { value: 0 as SqlBool, label: 'No', tone: 'bad' }
  ] as unknown as SegmentOption<T>[];

  let {
    label,
    value,
    options = YES_NO,
    clearable = true,
    disabled = false,
    hideLabel = false,
    compact = false,
    fill = true,
    onValue
  }: Props = $props();

  function pick(v: T) {
    if (disabled) return;
    if (clearable && value === v) onValue?.(null);
    else onValue?.(v);
  }
</script>

<div class="ynf" class:compact role="group" aria-label={label}>
  {#if !hideLabel}<span class="label">{label}</span>{/if}
  <div class="segments" class:fill>
    {#each options as opt (opt.value)}
      <button
        type="button"
        class="seg tone-{opt.tone ?? 'neutral'}"
        class:selected={value === opt.value}
        aria-pressed={value === opt.value}
        {disabled}
        onclick={() => pick(opt.value)}
      >
        {opt.label}
      </button>
    {/each}
  </div>
</div>

<style>
  .ynf {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  .label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--fg-muted, #3f4750);
    line-height: 1.2;
  }

  .segments {
    display: flex;
    gap: 0.375rem;
    flex-wrap: wrap;
  }

  .segments.fill .seg {
    flex: 1 1 0;
  }

  .seg {
    /* 3rem = a gloved thumb. Do not shrink this. */
    min-height: 3rem;
    min-width: 3.5rem;
    padding: 0.5rem 0.75rem;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 0.5rem;
    background: var(--surface, #fff);
    color: var(--fg, #10151b);
    cursor: pointer;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .compact .seg {
    min-height: 2.5rem;
    min-width: 3rem;
    padding: 0.375rem 0.5rem;
    font-size: 0.9375rem;
  }

  .seg:focus-visible {
    outline: 3px solid var(--accent, #0b63c5);
    outline-offset: 2px;
  }

  .seg:disabled {
    cursor: default;
    opacity: 0.75;
  }

  .seg.selected {
    color: #fff;
    border-color: transparent;
  }

  .seg.selected.tone-good {
    background: var(--good, #1a7f45);
  }
  .seg.selected.tone-bad {
    background: var(--bad, #b3261e);
  }
  .seg.selected.tone-warn {
    background: var(--warn, #8a4b00);
  }
  .seg.selected.tone-neutral {
    background: var(--fg, #10151b);
  }
</style>
